import { Injectable, inject, signal, computed } from '@angular/core';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import type { BreadcrumbItem } from '../components/breadcrumb/breadcrumb.component';

/**
 * Servicio que construye el breadcrumb desde la jerarquía de rutas (padre → hijo).
 * Opcionalmente un componente puede sobrescribir la etiqueta del último segmento con setCurrentLabel().
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);

  private readonly itemsSignal = signal<BreadcrumbItem[]>([]);
  private currentLabelOverride: string | null = null;

  /** Signal con los ítems del breadcrumb (sin incluir Inicio; el componente lo muestra fijo). */
  readonly breadcrumbItems = computed(() => this.itemsSignal());

  constructor() {
    this.buildFromRoute();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.buildFromRoute());
  }

  /**
   * Sobrescribe la etiqueta del último segmento (p. ej. "Empresa: Acme").
   * Llamar desde el componente en ngOnInit y clearCurrentLabel() en ngOnDestroy.
   */
  setCurrentLabel(label: string): void {
    this.currentLabelOverride = label;
    this.buildFromRoute();
  }

  clearCurrentLabel(): void {
    this.currentLabelOverride = null;
    this.buildFromRoute();
  }

  private buildFromRoute(): void {
    const snapshots = this.getRouteChain();
    const items: BreadcrumbItem[] = [];
    let pathSegments: string[] = [];

    for (let i = 0; i < snapshots.length; i++) {
      const sn = snapshots[i];
      const segments = sn.url.map((s) => s.path);
      pathSegments = [...pathSegments, ...segments];

      const isLast = i === snapshots.length - 1;
      const hasPath = segments.length > 0;
      // Incluir solo segmentos con path o el último (página actual), para no duplicar rutas con path ''
      if (!hasPath && !isLast) continue;

      const label = this.getLabel(sn, pathSegments, isLast);
      if (label == null || label === '') continue;

      const url = pathSegments.length > 0 ? '/' + pathSegments.join('/') : undefined;
      items.push({
        label,
        url: isLast ? undefined : url,
      });
    }

    this.itemsSignal.set(items);
  }

  private getRouteChain(): ActivatedRouteSnapshot[] {
    const chain: ActivatedRouteSnapshot[] = [];
    let s: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (s) {
      chain.push(s);
      s = s.firstChild;
    }
    return chain;
  }

  private getLabel(
    snapshot: ActivatedRouteSnapshot,
    pathSegments: string[],
    isLast: boolean
  ): string | null {
    const data = snapshot.data;
    const pathLabel =
      pathSegments.length > 0
        ? this.capitalize(pathSegments[pathSegments.length - 1])
        : null;

    if (isLast && this.currentLabelOverride != null) {
      return this.currentLabelOverride;
    }

    const breadcrumb = data['breadcrumb'];
    if (typeof breadcrumb === 'string' && breadcrumb.length > 0) return breadcrumb;
    const title = data['title'];
    if (typeof title === 'string' && title.length > 0) return title;
    if (pathLabel) return pathLabel;
    if (pathSegments.length === 0 && (breadcrumb || title)) return (breadcrumb || title) as string;
    return null;
  }

  private capitalize(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ');
  }
}
